import React, { useState } from "react";
import { MainPolicySettingBlock, PolicyCustomizeSection, SubPolicySettingBlock } from "uis/Section";
import UI from "@mile-meeting/mile-ui";
import { useToast } from "hook/useToast";
import AlertModal from "uis/modal/alert-modal";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { getToggleState } from "utils/meetingSettingUtils.service";
import MeetingRoomPolicyModal from "./MeetingRoomPolicyModal/MeetingRoomPolicyModal";
import { MeetingRoomSetting } from "./MeetingRoomPolicyModal/types";
import { getTabletAutoCheckOutDetail, updateTabletAutoCheckOutDetail } from "apis/meeting-settings/tablet";
import ToolTip from "uis/new-tootip";

type CheckOutPolicySettingProps = {
  hasMeetingRoomUsedTablet: boolean;
  autoCheckOutActive: GLOBAL_MEETING_SETTING_VALUE;
  autoCheckOutDetectionTime: number;
  autoCheckOutSoundAlertActive: GLOBAL_MEETING_SETTING_VALUE;
  onChangeAutoCheckOutActive: (active: GLOBAL_MEETING_SETTING_VALUE) => void;
  onChangeAutoCheckOutDetectionTime: (time: number) => void;
  onChangeAutoCheckOutSoundAlert: (checked: GLOBAL_MEETING_SETTING_VALUE) => void;
  onUpdate: () => void;
};

const CheckOutPolicySetting: React.FC<CheckOutPolicySettingProps> = ({
  hasMeetingRoomUsedTablet,
  autoCheckOutActive,
  autoCheckOutDetectionTime,
  autoCheckOutSoundAlertActive,
  onChangeAutoCheckOutActive,
  onChangeAutoCheckOutDetectionTime,
  onChangeAutoCheckOutSoundAlert,
  onUpdate,
}) => {
  const { showToast } = useToast();
  const [openAutoCheckOutDetectionTimeBulkModal, setOpenAutoCheckOutDetectionTimeBulkModal] = useState<boolean>(false);
  const [openCheckOutSoundAlertBulkModal, setOpenCheckOutSoundAlertBulkModal] = useState<boolean>(false);
  const [selectedAutoCheckOutDetectionTime, setSelectedAutoCheckOutDetectionTime] = useState<number>(0);
  const [autoCheckOutActiveModal, setAutoCheckOutActiveModal] = useState<boolean>(false);
  const [autoCheckOutUnActiveModal, setAutoCheckOutUnActiveModal] = useState<boolean>(false);
  const [openAutoCheckOutDetailSettingModal, setOpenAutoCheckOutDetailSettingModal] = useState<boolean>(false);
  //* 상세 설정 값 관련
  const [autoCheckOutSettingDetail, setAutoCheckOutSettingDetail] = useState<MeetingRoomSetting[]>([]);

  //* 자동 체크아웃 기능 상세 설정 조회
  const setAutoCheckOutDetailSetting = async () => {
    const res = await getTabletAutoCheckOutDetail();
    if (res.success && res.data) {
      setAutoCheckOutSettingDetail(res.data);
    }
  };

  //* 자동 체크아웃 기능 상세 설정 수정
  const onUpdateAutoCheckoutDetailSetting = async (meetingRoomSetting: MeetingRoomSetting[]) => {
    const res = await updateTabletAutoCheckOutDetail(meetingRoomSetting);
    if (res.success) {
      showToast("변경사항이 성공적으로 저장되었습니다.", "success");
      setOpenAutoCheckOutDetailSettingModal(false);
      onUpdate();
    } else {
      showToast("변경사항 저장에 실패했습니다.", "error");
    }
  };

  return (
    <PolicyCustomizeSection
      title="체크아웃 기능 설정"
      rightChildren={
        <UI.ButtonGroups.Text
          title="체크아웃 기능 더 알아보기"
          size="small"
          onClick={() =>
            window.open(
              "https://mile-corporation.notion.site/12ea222a9c1b819b9c04cac8e4f1d746#232a222a9c1b80189c56db79d5446886"
            )
          }
        />
      }
    >
      <MainPolicySettingBlock
        title="자동 체크아웃 기능 활성화"
        tagLabel="BETA"
        tagType="secondary"
        haveChildren={autoCheckOutActive !== GLOBAL_MEETING_SETTING_VALUE.OFF}
        showTag
        showAdvancedSettings
        onClickAdvancedSettings={() => {
          if (autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
            showToast("자동 체크아웃 기능을 활성화해야 상세 설정이 가능합니다.", "warning");
            return;
          }
          setAutoCheckOutDetailSetting();
          setOpenAutoCheckOutDetailSettingModal(true);
        }}
        disabledAdvancedSettings={autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.OFF}
        description="활성화 시 회의실 내부의 음성으로 회의 지속 여부를 판단합니다."
        rightChildren={
          <ToolTip
            text={
              autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.ON
                ? "전체 회의실 일괄 설정된 상태입니다."
                : autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                  ? "회의실별 상세 설정된 상태입니다."
                  : undefined
            }
            position={{
              top: -30,
              left: autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
            }}
            icon={
              <div style={{ position: "relative" }}>
                <UI.Selection.Toggle.TriState
                  size="large"
                  state={getToggleState(autoCheckOutActive)}
                  onChange={() =>
                    autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                      ? setAutoCheckOutActiveModal(true)
                      : setAutoCheckOutUnActiveModal(true)
                  }
                  disabled={!hasMeetingRoomUsedTablet}
                />
                {!hasMeetingRoomUsedTablet && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={() =>
                      showToast(
                        "자동 체크아웃 기능은 최소 1개 이상의 회의실이 내부 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                        "warning"
                      )
                    }
                  />
                )}
              </div>
            }
          />
        }
      />
      {autoCheckOutActive !== GLOBAL_MEETING_SETTING_VALUE.OFF && (
        <SubPolicySettingBlock
          title="음성 감지 시간"
          description="설정한 시간 동안 음성이 감지 되지 않으면, 회의가 자동으로 종료됩니다."
          rightChildren={
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", zIndex: 51 }}>
              <UI.SelectBox
                size="small"
                data={["5분", "10분", "15분"]}
                selectedData={[autoCheckOutDetectionTime === -1 ? "-" : autoCheckOutDetectionTime + "분"]}
                width={120}
                dropdownMaxHeight={120}
                onChange={(time: string[]) => {
                  if (autoCheckOutDetectionTime === -1) {
                    setOpenAutoCheckOutDetectionTimeBulkModal(true);
                    setSelectedAutoCheckOutDetectionTime(Number(time[0].replaceAll("분", "")));
                    return;
                  }
                  onChangeAutoCheckOutDetectionTime(Number(time[0].replaceAll("분", "")));
                }}
                disabled={!hasMeetingRoomUsedTablet}
              />
              {autoCheckOutDetectionTime === -1 && (
                <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                  회의실별 상세 설정된 상태입니다.
                </UI.Typography>
              )}
            </div>
          }
        />
      )}
      {autoCheckOutActive !== GLOBAL_MEETING_SETTING_VALUE.OFF && (
        <SubPolicySettingBlock
          title="자동 체크아웃 전 소리 알림 활성화"
          description="자동 체크아웃 1분 전, 태블릿에서 알림 소리가 울립니다."
          rightChildren={
            <ToolTip
              text={
                autoCheckOutSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON
                  ? "전체 회의실 일괄 설정된 상태입니다."
                  : autoCheckOutSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                    ? "회의실별 상세 설정된 상태입니다."
                    : undefined
              }
              position={{
                top: -30,
                left: autoCheckOutSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
              }}
              icon={
                <div style={{ position: "relative" }}>
                  <UI.Selection.Toggle.TriState
                    size="large"
                    state={getToggleState(autoCheckOutSoundAlertActive)}
                    onChange={() => {
                      if (autoCheckOutSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL) {
                        setOpenCheckOutSoundAlertBulkModal(true);
                        return;
                      }
                      onChangeAutoCheckOutSoundAlert(
                        autoCheckOutSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                          ? GLOBAL_MEETING_SETTING_VALUE.ON
                          : GLOBAL_MEETING_SETTING_VALUE.OFF
                      );
                    }}
                    disabled={!hasMeetingRoomUsedTablet}
                  />
                  {!hasMeetingRoomUsedTablet && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      onClick={() =>
                        showToast(
                          "태블릿 정책은 최소 1개 이상의 회의실이 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                          "warning"
                        )
                      }
                    />
                  )}
                </div>
              }
            />
          }
        />
      )}
      <MeetingRoomPolicyModal
        modalIsOpen={openAutoCheckOutDetailSettingModal}
        closeModal={() => setOpenAutoCheckOutDetailSettingModal(false)}
        title="자동 체크아웃 기능 회의실별 상세 설정"
        settingData={autoCheckOutSettingDetail}
        onUpdate={onUpdateAutoCheckoutDetailSetting}
        metaData={[
          {
            key: "isAutoCheckOutEnabled",
            label: `자동 체크아웃\n기능 활성화`,
            isMainPolicy: true,
            type: "toggle",
          },
          {
            key: "autoCheckOutDetectionTime",
            label: `음성 감지 시간`,
            type: "select",
            options: [5, 10, 15],
            disabledWhen: (data: MeetingRoomSetting) => {
              return !data["isAutoCheckOutEnabled"];
            },
          },
          {
            key: "isAutoCheckOutSoundAlertEnabled",
            label: `자동 체크아웃 전\n소리 알림 활성화`,
            type: "toggle",
            disabledWhen: (data: MeetingRoomSetting) => {
              return !data["isAutoCheckOutEnabled"];
            },
          },
        ]}
      />
      <AlertModal
        title={
          <p>
            자동 체크아웃 기능을
            <br />
            활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            음성 감지를 통해 체크아웃을 자동으로
            <br />
            처리할 수 있도록 보조합니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeAutoCheckOutActive(GLOBAL_MEETING_SETTING_VALUE.ON);
          setAutoCheckOutActiveModal(false);
        }}
        modalIsOpen={autoCheckOutActiveModal}
        closeModal={() => setAutoCheckOutActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            자동 체크아웃 기능을
            <br />
            {autoCheckOutActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL ? "일괄" : ""} 비활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            자동으로 체크아웃을 처리하는
            <br />
            음성 감지 기능을 종료합니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeAutoCheckOutActive(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setAutoCheckOutUnActiveModal(false);
        }}
        modalIsOpen={autoCheckOutUnActiveModal}
        closeModal={() => setAutoCheckOutUnActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            음성 감지 시간을
            <br />
            일괄 변경하시겠습니까?
          </p>
        }
        content={"회의실별 상세 설정된 값이 일괄 변경됩니다."}
        btnTitle="변경하기"
        onChange={() => {
          setOpenAutoCheckOutDetectionTimeBulkModal(false);
          onChangeAutoCheckOutDetectionTime(selectedAutoCheckOutDetectionTime);
        }}
        modalIsOpen={openAutoCheckOutDetectionTimeBulkModal}
        closeModal={() => setOpenAutoCheckOutDetectionTimeBulkModal(false)}
      />
      <AlertModal
        title={
          <p>
            자동 체크아웃 전 소리 알림을
            <br />
            일괄 비활성화하시겠습니까?
          </p>
        }
        content="회의실별 상세 설정된 값이 일괄 변경됩니다."
        btnTitle="변경하기"
        onChange={() => {
          onChangeAutoCheckOutSoundAlert(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setOpenCheckOutSoundAlertBulkModal(false);
        }}
        modalIsOpen={openCheckOutSoundAlertBulkModal}
        closeModal={() => setOpenCheckOutSoundAlertBulkModal(false)}
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(CheckOutPolicySetting);
